import { __rest } from "tslib";
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { exposeTestId } from '../../config';
import { useI18n } from '../../locale/I18nContext';
import { BusyState } from '../../store/busy/BusyState';
import { selectBusyState, selectLayoutState, selectStatusState, selectWarningState, } from '../../store/selectors';
import { StatusState } from '../../store/status/StatusState';
import { ObjectStatus } from '../ObjectStatus';
import { getFirstFocusable } from '@sas/nova-commons/util/focus';
import cn from './StatusLayout.css.json';
import './StatusLayout.css';
export const StatusLayoutBase = (_a) => {
    var { warningState, statusState, busyState, parentBusyState } = _a, props = __rest(_a, ["warningState", "statusState", "busyState", "parentBusyState"]);
    const localize = useI18n();
    const error = warningState.type === 'error' ? warningState.message : null;
    const information = warningState.type === 'information' ? warningState.message : null;
    const [busyTimerExpired, setBusyTimerExpired] = useState(true); // starting with true to show pill immediately if busy on mount
    const [hasBusyHover, setHasBusyHover] = useState(false);
    let statusPillProps;
    let statusOverlayVisible = true;
    const isBusyState = (busyState === null || busyState === void 0 ? void 0 : busyState.isBusy) && !(parentBusyState === null || parentBusyState === void 0 ? void 0 : parentBusyState.isBusy);
    if (error) {
        statusPillProps = {
            mode: 'error',
            text: error,
        };
    }
    else if (information) {
        statusPillProps = {
            mode: 'information',
            text: information,
        };
    }
    else if (statusState) {
        statusPillProps = {
            mode: statusState.type,
            onPress: statusState.isPressable
                ? () => props.statusActions.notifyPress(props.id)
                : undefined,
        };
    }
    else if (busyState && isBusyState) {
        // Only render the busy pill once the timer has expired, or immediately if isPaused.
        // indicatorVisible==false will hide it regardless of state.
        if (busyState.indicatorVisible && (busyTimerExpired || busyState.isPaused)) {
            if (busyState.isPaused) {
                statusPillProps = {
                    mode: hasBusyHover ? 'resumeLoading' : 'loadingCanceled',
                    'aria-label': localize({ key: 'ObjectStatus.resumeLoading.txt' }),
                };
            }
            else if (busyState.canPause) {
                statusPillProps = {
                    mode: hasBusyHover ? 'cancelLoading' : 'busy',
                    'aria-label': localize({ key: 'ObjectStatus.cancelLoading.txt' }),
                };
            }
            else {
                statusPillProps = {
                    mode: 'busy',
                };
            }
            // If the busy state can be paused then handle hover and press
            if (busyState.canPause) {
                statusPillProps.onMouseEnter = () => setHasBusyHover(true);
                statusPillProps.onMouseLeave = () => setHasBusyHover(false);
                statusPillProps.onPress = () => props.busyActions.togglePaused(props.id);
            }
        }
    }
    else {
        statusOverlayVisible = false;
    }
    // Any time the state changes from non-busy to busy, set the timer to show the busy pill
    useEffect(() => {
        if (!isBusyState) {
            setBusyTimerExpired(false);
            return;
        }
        const timerId = setTimeout(() => setBusyTimerExpired(true), 350);
        return () => {
            clearTimeout(timerId);
        };
    }, [isBusyState]);
    // If rendering a status pill then reduce opacity of other content. The one exception is if
    // the parent visual is busy, in which case there is an opacity already applied at the parent.
    const contentOpacity = statusOverlayVisible && statusPillProps && !(parentBusyState === null || parentBusyState === void 0 ? void 0 : parentBusyState.isBusy) ? 0.3 : undefined;
    // Move focus to content if the pill has focus when it unmounts
    const refContent = useRef(null);
    const refPillHasFocus = useRef(false);
    const hasPill = !!statusPillProps;
    useEffect(() => {
        var _a;
        if (!hasPill && refPillHasFocus.current && refContent.current) {
            (_a = getFirstFocusable(refContent.current)) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [hasPill]);
    // If any ancestor has a status overlay then propagate that down to descendants
    // through the AncestorStatusContext provider.
    const hasAncestorStatusOverlay = useHasAncestorStatusOverlay();
    return (React.createElement(Fragment, null,
        React.createElement("div", { ref: refContent, className: cn.content, style: {
                // we still render the component so we can hopefully get reasonable constraints.
                // Use visibility:hidden instead of display:none so that the layout height of center justified
                // prompt header content doesn't change when in an error state.
                visibility: error ? 'hidden' : undefined,
                opacity: contentOpacity,
            } },
            React.createElement(AncestorStatusContext.Provider, { value: hasAncestorStatusOverlay || statusOverlayVisible }, props.children)),
        // When busyState.isBusy is true but busyState.indicatorVisible is false,
        // we still need to render the statusOverlay in order to block mouse input
        // to the content.
        statusOverlayVisible ? (React.createElement("div", { className: cn.statusOverlay, "data-testid": isBusyState && exposeTestId() ? 'busyOverlay' : undefined, onFocus: () => (refPillHasFocus.current = true), onBlur: () => (refPillHasFocus.current = false), 
            // If this is a container then we set pointerEvents:none so that all
            // mouse events flow through to child visuals. This allows those visuals to
            // still support the object context menu.
            style: { pointerEvents: props.isContainer ? 'none' : undefined } }, statusPillProps ? (React.createElement(ObjectStatus, Object.assign({ style: { pointerEvents: 'auto' } }, statusPillProps))) : null)) : null));
};
export const StatusLayout = connect((state, props) => ({
    busyState: selectBusyState(state, props.id),
    parentBusyState: selectBusyState(state, selectLayoutState(state, props.id).compositeParentKey || ''),
    statusState: selectStatusState(state, props.id),
    warningState: selectWarningState(state, props.id),
}), (dispatch) => ({
    statusActions: bindActionCreators(StatusState.actions, dispatch),
    busyActions: bindActionCreators(BusyState.actions, dispatch),
}))(StatusLayoutBase);
// This context is used to pass the status overlay state down to child visuals.
// This is needed so that composite visual children can block their own mouse events,
// since we intentionally don't do that at the composite/container level.
const AncestorStatusContext = React.createContext(false);
export function useHasAncestorStatusOverlay() {
    return useContext(AncestorStatusContext);
}
